import { Box, FormHelperText, styled, Typography } from '@mui/material';
import { Option } from '@api/types/option';
import { DefaultTFuncReturn } from 'i18next';
import { Gap } from '@components/layout-util-components/gap';

interface HelperIndicators {
  left: string;
  right: string;
}

interface Props<T> {
  name: string;
  label?: string | DefaultTFuncReturn;
  error?: string;
  options: Array<Option<T>>;
  value: T;
  onChange: (value?: T) => void;
  readonly?: boolean;
  helperIndicators?: HelperIndicators;
}

const StyledContainer = styled('div', {
  target: 'inline-radio-select-group-container',
})({
  display: 'flex',
  flexDirection: 'row',
  gap: 5,
  minHeight: 40,
  alignItems: 'stretch',

  'input[type="radio"]': {
    '-webkit-appearance': 'none',
    appearance: 'none',
    margin: 0,
  },
});

const StyledIndicators = styled('div')({
  fontFamily: 'Roboto',
  fontSize: '10px',
  fontWeight: '500',
  lineHeight: '14px',
  letterSpacing: '0px',
  textAlign: 'left',
  display: 'flex',
  justifyContent: 'space-between',
  color: '#00000099',
});

const StyledOption = styled('label', {
  target: 'inline-radio-select-group-option',
})<{
  isSelected: boolean;
}>(({ isSelected, theme: { palette } }) => ({
  flex: '1 1 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 8,
  border: '1px solid #00000040',
  borderRadius: 4,
  backgroundColor: palette.common.white,
  color: palette.common.grey90,
  cursor: 'pointer',
  fontFamily: 'Roboto',
  fontSize: '12px',
  fontWeight: '500',
  lineHeight: '26px',
  letterSpacing: '0.46000000834465027px',
  textAlign: 'left',

  ...(isSelected && {
    border: '2px solid #000000BF',
    padding: 7,
    cursor: 'default',
  }),
}));

export default function InlineRadioSelectGroup<T>({
  name,
  options,
  label,
  error,
  onChange,
  value,
  readonly,
  helperIndicators,
}: Props<T>) {
  return (
    <>
      {label ? (
        <>
          <Typography>{label}</Typography>
          <Gap size={15} />
        </>
      ) : null}
      <StyledContainer>
        {options.map(({ id, label }) => {
          const isSelected = id === value;

          return (
            <StyledOption
              key={`${id}`}
              isSelected={isSelected}
              onClick={!readonly ? () => onChange(id) : undefined}
            >
              <input
                type="radio"
                name={name}
                checked={isSelected}
                readOnly={readonly}
                onChange={(e) =>
                  e.target.checked ? onChange(id) : onChange(undefined)
                }
              />
              {label}
            </StyledOption>
          );
        })}
      </StyledContainer>
      {helperIndicators != null ? (
        <>
          <Gap size={6} />
          <StyledIndicators>
            <span>{helperIndicators!.left}</span>
            <span>{helperIndicators!.right}</span>
          </StyledIndicators>
        </>
      ) : null}
      {error ? (
        <Box mx={2}>
          <FormHelperText error>{error}</FormHelperText>
        </Box>
      ) : null}
    </>
  );
}
